import type {
  Accomplishment,
  AccomplishmentsResponse,
  AwardInput,
  Claim,
  ClaimsResponse,
  ClaimStatus,
  CreateAccomplishmentInput,
  CreateRosterEntryInput,
  CrewMemberAdmin,
  UpdateCrewMemberProfileInput,
  UploadedFlagsResponse,
  CrewProfile,
  MemberSummary,
  MembersResponse,
  MyProfile,
  PutShipPlanInput,
  RecruitsResponse,
  TurnedAwayResponse,
  ReorderRosterInput,
  ReviewClaimInput,
  RosterManageEntry,
  RosterManageResponse,
  RosterResponse,
  SetRoleInput,
  ShipPlan,
  ShipPlansResponse,
  SubmitClaimInput,
  UpdateAccomplishmentInput,
  UpdateProfileInput,
  UpdateRosterEntryInput,
  UploadFlagResponse,
  User,
} from '@clan-heart/shared';
import { buildQueryString } from './lib/queryString.js';

/**
 * Everything lives under /api on the same origin as the page, so a session
 * cookie set by the server is sent back automatically — `credentials:
 * 'same-origin'` is enough, no CORS and no bearer token to manage here.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (response.status === 204) {
    return undefined as T;
  }
  if (!response.ok) {
    const message = await errorMessage(response);
    throw new Error(message);
  }
  return (await response.json()) as T;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string' && body.error.length > 0) return body.error;
  } catch {
    // Not a JSON error body — fall through to the status fallback.
  }
  return `Request failed (${response.status})`;
}

// ---------------------------------------------------------------------------
// session
// ---------------------------------------------------------------------------

/** Resolves to `null` when signed out, rather than throwing on 401. */
export async function getMe(): Promise<User | null> {
  const response = await fetch('/api/me', { credentials: 'same-origin' });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(await errorMessage(response));
  return (await response.json()) as User;
}

/**
 * Starting sign-in is a navigation, not a fetch: the server's 302 chain ends
 * at Discord, which a fetch() call cannot follow across origins.
 */
export const DISCORD_SIGNIN_URL = '/api/auth/discord/start';

export async function logout(): Promise<void> {
  await request<void>('/api/auth/logout', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// accomplishments
// ---------------------------------------------------------------------------

export async function getAccomplishments(opts?: {
  includeArchived?: boolean;
}): Promise<AccomplishmentsResponse> {
  const qs = buildQueryString({ includeArchived: opts?.includeArchived });
  return request<AccomplishmentsResponse>(`/api/accomplishments${qs}`);
}

export async function createAccomplishment(
  input: CreateAccomplishmentInput,
): Promise<Accomplishment> {
  return request<Accomplishment>('/api/accomplishments', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateAccomplishment(
  id: string,
  input: UpdateAccomplishmentInput,
): Promise<Accomplishment> {
  return request<Accomplishment>(`/api/accomplishments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** A manager awards an accomplishment directly to a crew member. */
export async function awardAccomplishment(id: string, input: AwardInput): Promise<{ claimId: string; approvedPending: boolean }> {
  return request<{ claimId: string; approvedPending: boolean }>(`/api/accomplishments/${id}/award`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function claimAccomplishment(
  id: string,
  input: SubmitClaimInput,
): Promise<Claim> {
  return request<Claim>(`/api/accomplishments/${id}/claims`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// claims
// ---------------------------------------------------------------------------

export async function getClaims(opts?: {
  status?: ClaimStatus;
  mine?: boolean;
}): Promise<ClaimsResponse> {
  const qs = buildQueryString({ status: opts?.status, mine: opts?.mine });
  return request<ClaimsResponse>(`/api/claims${qs}`);
}

export async function reviewClaim(id: string, input: ReviewClaimInput): Promise<Claim> {
  return request<Claim>(`/api/claims/${id}/review`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function withdrawClaim(id: string): Promise<void> {
  await request<void>(`/api/claims/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// members
// ---------------------------------------------------------------------------

export async function getMembers(): Promise<MembersResponse> {
  return request<MembersResponse>('/api/members');
}

export async function setMemberRole(id: string, role: SetRoleInput['role']): Promise<MemberSummary> {
  return request<MemberSummary>(`/api/members/${id}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

// ---------------------------------------------------------------------------
// roster
// ---------------------------------------------------------------------------

/** GET /api/roster — public, drives both the About page and this. */
export async function getRoster(): Promise<RosterResponse> {
  return request<RosterResponse>('/api/roster');
}

/** GET /api/roster/manage — owner/officer-family only; includes the Discord link. */
export async function getRosterManage(): Promise<RosterManageResponse> {
  return request<RosterManageResponse>('/api/roster/manage');
}

export async function createRosterEntry(input: CreateRosterEntryInput): Promise<RosterManageEntry> {
  return request<RosterManageEntry>('/api/roster', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateRosterEntry(
  id: string,
  input: UpdateRosterEntryInput,
): Promise<RosterManageEntry> {
  return request<RosterManageEntry>(`/api/roster/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteRosterEntry(id: string): Promise<void> {
  await request<void>(`/api/roster/${id}`, { method: 'DELETE' });
}

export async function reorderRoster(input: ReorderRosterInput): Promise<void> {
  await request<void>('/api/roster/reorder', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * POST /api/flags — multipart, not JSON, so this bypasses `request()`
 * entirely: setting a `Content-Type: application/json` header (as `request`
 * does for any other body) would stomp the multipart boundary the browser
 * generates for a `FormData` body.
 */
export async function uploadFlag(file: File): Promise<UploadFlagResponse> {
  const body = new FormData();
  body.append('file', file);
  const response = await fetch('/api/flags', {
    method: 'POST',
    credentials: 'same-origin',
    body,
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }
  return (await response.json()) as UploadFlagResponse;
}

// ---------------------------------------------------------------------------
// recruits
// ---------------------------------------------------------------------------

export async function getRecruits(): Promise<RecruitsResponse> {
  return request<RecruitsResponse>('/api/recruits');
}

interface DecideRecruitResponse {
  outcome: 'approved' | 'rejected';
  decidedBy: string;
  alreadyDecided: boolean;
}

export async function approveRecruit(userId: string): Promise<DecideRecruitResponse> {
  return request<DecideRecruitResponse>(`/api/recruits/${userId}/approve`, { method: 'POST' });
}

export async function rejectRecruit(userId: string): Promise<DecideRecruitResponse> {
  return request<DecideRecruitResponse>(`/api/recruits/${userId}/reject`, { method: 'POST' });
}

/** Everyone rejected or removed, newest decision first (managers). */
export async function getTurnedAway(): Promise<TurnedAwayResponse> {
  return request<TurnedAwayResponse>('/api/recruits/turned-away');
}

/** Let someone turned away by mistake aboard after all (managers). */
export async function readmitCrew(userId: string): Promise<{ profileSlug: string }> {
  return request<{ profileSlug: string }>(`/api/recruits/${userId}/readmit`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// ship plans
// ---------------------------------------------------------------------------

/** GET /api/ships/plans — public; the Ships admin page and (later) profiles both read this. */
export async function getShipPlans(): Promise<ShipPlansResponse> {
  return request<ShipPlansResponse>('/api/ships/plans');
}

export async function putShipPlan(shipId: string, input: PutShipPlanInput): Promise<ShipPlan> {
  return request<ShipPlan>(`/api/ships/${shipId}/plan`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteShipPlan(shipId: string): Promise<void> {
  await request<void>(`/api/ships/${shipId}/plan`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// crew profiles
// ---------------------------------------------------------------------------

/** GET /api/crew/:slug — public; resolves to `null` on 404 (unknown slug or
 * a non-approved member) rather than throwing, so ProfilePage can tell that
 * apart from a real request failure. */
export async function getCrewProfile(slug: string): Promise<CrewProfile | null> {
  const response = await fetch(`/api/crew/${encodeURIComponent(slug)}`, { credentials: 'same-origin' });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await errorMessage(response));
  return (await response.json()) as CrewProfile;
}

/** GET /api/me/profile — the signed-in member's own card (see `MyProfile` in
 * @clan-heart/shared for the full shape: slug, name, rank, section, role,
 * flag, motto, favouriteShip, flagshipName). */
export async function getMyProfile(): Promise<MyProfile> {
  return request<MyProfile>('/api/me/profile');
}

/** PATCH /api/me/profile — send only the fields that changed; a 409 means
 * the chosen crew name is already taken by another roster entry. */
export async function updateMyProfile(input: UpdateProfileInput): Promise<MyProfile> {
  return request<MyProfile>('/api/me/profile', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// officer controls (a manager editing someone else's crew profile)
// ---------------------------------------------------------------------------

/**
 * GET /api/crew/:slug/manage — owner/officer-family only; drives the
 * "Officer controls" panel on a public profile. Resolves to `null` on a 404
 * (unknown slug) or 403 (viewer isn't a manager after all) rather than
 * throwing, so the panel simply doesn't render instead of surfacing an
 * error to a member who happens to land here.
 */
export async function getCrewMemberAdmin(slug: string): Promise<CrewMemberAdmin | null> {
  const response = await fetch(`/api/crew/${encodeURIComponent(slug)}/manage`, { credentials: 'same-origin' });
  if (response.status === 404 || response.status === 403) return null;
  if (!response.ok) throw new Error(await errorMessage(response));
  return (await response.json()) as CrewMemberAdmin;
}

/** PATCH /api/crew/:slug/manage — a manager sets a member's motto, favourite ship and flagship name. */
export async function updateCrewMemberProfile(slug: string, input: UpdateCrewMemberProfileInput): Promise<CrewMemberAdmin> {
  return request<CrewMemberAdmin>(`/api/crew/${encodeURIComponent(slug)}/manage`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** GET /api/flags/manage — every uploaded flag with who flies it (managers). */
export async function getUploadedFlags(): Promise<UploadedFlagsResponse> {
  return request<UploadedFlagsResponse>('/api/flags/manage');
}

/** DELETE /api/flags/:id — `flag` is the flag's path, `/api/flags/<id>`. 409 while someone flies it. */
export async function deleteUploadedFlag(flag: string): Promise<void> {
  await request<void>(flag, { method: 'DELETE' });
}

/** POST /api/crew/:slug/ship-click — one more click on this ship's shared count; every 5th (by anyone) brings a storm, every 12th sinks it. Fires if the server can't be reached. */
export async function clickShip(slug: string): Promise<'fire' | 'storm' | 'sink'> {
  try {
    const response = await fetch(`/api/crew/${encodeURIComponent(slug)}/ship-click`, { method: 'POST', credentials: 'same-origin' });
    if (!response.ok) return 'fire';
    const body = (await response.json()) as { action?: string };
    return body.action === 'sink' || body.action === 'storm' ? body.action : 'fire';
  } catch {
    return 'fire';
  }
}

/** POST /api/crew/:slug/broadside — a broadside was fired from this profile's ship. Fire and forget: it's a tally, never worth an error. */
export function reportBroadside(slug: string): void {
  void fetch(`/api/crew/${encodeURIComponent(slug)}/broadside`, { method: 'POST', credentials: 'same-origin' }).catch(() => {});
}

/** GET /api/flags response — every uploaded flag path, newest first. Feeds a
 * flag picker alongside the 13 fixed clan flags under `/art/flags`. */
export interface FlagListResponse {
  flags: string[];
}

export async function getFlagList(): Promise<FlagListResponse> {
  return request<FlagListResponse>('/api/flags');
}
